import type { SearchParams, YadoHotel } from "../types";
import { getImage } from "../../utils/pathHelper";
import styles from "../styles/HotelSearch.module.scss"
import mapPinBlueSvg from '../images/mapPinBlue.svg';
import plusCircleSvg from '../images/plusCircle.svg';
import skiLiftSvg from '../images/skiLift.svg';
import clockSvg from '../images/clock.svg';
import homeSvg from '../images/home.svg';
import checkGreenSvg from '../images/checkGreen.svg';
import infoSvg from '../images/info.svg';
import i18n, { locale_underscore } from "../../i18n";
import { findRoomWithMaxPrice, findRoomWithMinPrice, formatQuery, getAdultCountDisplay, getChildrenCountDisplay } from "../utils";
import { differenceInDays, format, subDays } from "date-fns";
import { formatDateWithDayOfWeek } from "../../utils/dateHelper";

type HotelItemProps = {
  hotel: YadoHotel
  query: SearchParams
  sortBy: string
  fetchedQuery: SearchParams
  onClickArea: (e: React.MouseEvent<HTMLDivElement>, areaCode: string) => void;
};

const HotelItem: React.FC<HotelItemProps> = ({ hotel, query, fetchedQuery, sortBy, onClickArea }) => {
  const onClickCard = () => {
    const formattedQuery = formatQuery(query);
    const searchParams = new URLSearchParams(formattedQuery).toString();
    window.location.href = `/snow/stay-packages/hotels/${hotel.yado_id}?${searchParams}`;
  }

  const roomAccordingToSort = () => {
    switch (sortBy) {
      case 'priceAsc':
        return findRoomWithMinPrice(hotel.yado_rooms)
      case 'priceDesc':
        return findRoomWithMaxPrice(hotel.yado_rooms)
      default:
        // デフォルトは安い順
        return findRoomWithMinPrice(hotel.yado_rooms)
    }
  }

  const sortPackages = (packages) => {
    switch (sortBy) {
      case 'priceAsc':
        return packages.sort((a, b) => a.total_price.amount.replace(',', '') - b.total_price.amount.replace(',', ''));
      case 'priceDesc':
        return packages.sort((a, b) => b.total_price.amount.replace(',', '') - a.total_price.amount.replace(',', ''));
      default:
        {
          return packages.sort((a, b) => {
            const nearestResortCode = hotel.nearest_resort_code
            const is_a_nearest_single_ticket = (a.snow_item.related_resorts.length === 1 && a.snow_item.related_resorts[0].code === nearestResortCode) ? 1 : 0
            const is_b_nearest_single_ticket = (b.snow_item.related_resorts.length === 1 && b.snow_item.related_resorts[0].code === nearestResortCode) ? 1 : 0

            // ホテルの近隣スキー場が含まれているものが上
            if (is_a_nearest_single_ticket > is_b_nearest_single_ticket) return -1
            if (is_b_nearest_single_ticket > is_a_nearest_single_ticket) return 1

            const price_a = Number(a.total_price.amount.replace(',', ''))
            const price_b = Number(b.total_price.amount.replace(',', ''))

            // その中で安い方が上
            if (price_a < price_b) return -1
            if (price_b < price_a) return 1
          });
        }
    }
  }

  const anyPlanCancellable = hotel.yado_rooms.some((yado_room) => yado_room.yado_plan.cancellable)
  const room = roomAccordingToSort();
  const packages = sortPackages(room.yado_plan.packages);
  const isDateSelected = fetchedQuery.checkinDate && fetchedQuery.checkoutDate;
  const maxClosingDays = Math.max(...hotel.yado_rooms.map(room => room.yado_plan.closing_days_before_checkin));

  const peopleAndStayCountDisplay = (minimumStayDays) => {
    if (i18n.locale === 'en') {
      const adultCountString = fetchedQuery.people.adult === 1 ? `1${i18n.t('base.adult')}`: `${fetchedQuery.people.adult}${i18n.t('base.adults')}`;
      const childCountString = fetchedQuery.people.children.length === 1 ? `1${i18n.t('base.child')}`: `${fetchedQuery.people.children.length}${i18n.t('base.children')}`;
      if (fetchedQuery.people.children.length === 0) { return `${adultCountString} ${stayCountString(minimumStayDays)}`; }

      return [adultCountString, childCountString, stayCountString(minimumStayDays)].filter(Boolean).join(' ');
    }

    const adultCountString = getAdultCountDisplay(fetchedQuery.people.adult).replace(' ', '');
    const childCountString = getChildrenCountDisplay(fetchedQuery.people.children.length).replace(' ', '');
    if (fetchedQuery.people.children.length === 0) { return `${adultCountString} ${stayCountString(minimumStayDays)}`; }

    return [adultCountString, childCountString, stayCountString(minimumStayDays)].filter(Boolean).join(' ');
  }

  const stayCountString = (minimumStayDays) => {
    const stayCount = isDateSelected ? differenceInDays(fetchedQuery.checkoutDate, fetchedQuery.checkinDate) : minimumStayDays;
    if (stayCount === 1) {
      return i18n.t('base.number_of_stays.one')
    } else {
      return i18n.t('base.number_of_stays.other', { count: stayCount })
    }
  }

  return (
    <div className={styles.hotelItem} onClick={onClickCard} onKeyPress={onClickCard} >
      <div className={`${styles.hotelInfo}`}>
        <div className={`${styles.hotelImageWrapper} col-4 col-md-3`}>
          <div className={styles.hotelImage}>
            <img src={hotel.images[0]} alt={hotel.name} />

            {hotel.ski_in_out &&
              <span className={styles.skiInOut}>
                <img src={getImage(skiLiftSvg)} alt='' className="mr-1" />
                {i18n.t('accommodations.ski_in_out')}
              </span>
            }
          </div>
        </div>
        <div className="col-8 col-md-9 pl-md-0 container my-3">
          <h2 className={styles.hotelName}>{hotel.name}</h2>
          <div className={styles.hotelArea} onClick={(e) => onClickArea(e, hotel.area_code)}>
            <img src={getImage(mapPinBlueSvg)} alt='' className="mr-2" height={20} />
            <div>{i18n.t(`areas.${hotel.area_code}`)}</div>
          </div>
          {hotel.resorts.map((resort, i) => {
            const access = hotel.snow_resort_accesses.find(access => resort.code === access.resort_code)

            if (access) {
              return (
                <p key={`${hotel.name}_accesses_${i}`} className={styles.hotelAccess}>{resort?.localized_name[locale_underscore]} {access.summary}</p>
              )
            }
          })}
          <div className={`d-flex align-items-center ${anyPlanCancellable ? 'justify-content-between' : 'justify-content-end'}`}>
            {anyPlanCancellable &&
              <div className={styles.hotelFreeCancel}>
                <img src={getImage(checkGreenSvg)} className="mr-2" alt="check icon" />
                <small className="text-sm">{i18n.t('snow_items.cancel_policy.free_cancellation')}</small>
              </div>
            }
            <button
              type='button'
              className="col-3 btn btn-primary rounded-pill font-weight-bold float-right d-none d-md-block"
              style={{minWidth: 'fit-content'}}
              >
              {i18n.t('stay_packages.hotels.index.details_reservation')}
            </button>
          </div>
          <div className={`${styles.closingDeadline} d-none d-md-flex my-3`}>
            <img src={getImage(clockSvg)} className="mr-2" alt="check icon" />
            {isDateSelected ? (
              <div>{i18n.t('stay_packages.shared.reservation_deadline_until', {x: formatDateWithDayOfWeek(subDays(query.checkinDate, maxClosingDays))})}</div>
            ) : (
              <div>{i18n.t('stay_packages.shared.reservation_deadline_up_to', {x: maxClosingDays})}</div>
            )}
          </div>
        </div>
      </div>
      <div className={`${styles.packageInfo} d-lg-flex flex-grow-1 mb-2`}>
        <div className="col-12 col-md-3 container mb-2">
          <div className={`${styles.closingDeadline} d-md-none my-3`}>
            <img src={getImage(clockSvg)} className="mr-2" alt="check icon" />
            {isDateSelected ? (
              <div>{i18n.t('stay_packages.shared.reservation_deadline_until', {x: formatDateWithDayOfWeek(subDays(query.checkinDate, maxClosingDays))})}</div>
            ) : (
              <div>{i18n.t('stay_packages.shared.reservation_deadline_up_to', {x: maxClosingDays})}</div>
            )}
          </div>
          <p className="font-weight-bold m-0 d-flex align-items-center my-2">
            <img src={getImage(homeSvg)} alt="" className="mr-1" />
            <span>{room.name}</span>
          </p>
          {room.room_information.bed_type_keys.map((bedType, i) => (
            <p className="m-0 bg-light py-1 px-2 rounded-xl" key={`${hotel.id}-${bedType}`}>
              {i18n.t(`accommodations.bed_type.${bedType}`)} × {room.room_information.number_of_beds[i]}
            </p>
          ))}
        </div>

        <ul className="col-12 col-md-9 container pl-md-0 m-0">
          {packages.slice(0, 3).map((packageItem) => (
            <li key={packageItem.id} className={`${styles.hotelPackage}`}>
              <div className="d-flex align-items-center mb-1 justify-content-between">
                <div className="d-flex pr-3">
                  <img src={getImage(plusCircleSvg)} alt="" className="mr-1" />
                  <div className="d-flex flex-column ml-2">
                    <span className="font-weight-bold">
                      {room.yado_plan.included_lift_ticket
                        ? room.yado_plan.included_lift_ticket.localized_name[locale_underscore]
                        : packageItem.snow_item.name}
                    </span>
                    <span>{packageItem?.snow_item && packageItem.common_resorts[0]?.localized_name[locale_underscore]}</span>
                  </div>
                </div>

                <div className="d-flex flex-column">
                  {i18n.locale === 'ja' ? (
                    <div className="d-flex align-items-baseline justify-content-end">
                      <div className={styles.hotelPrice}>{packageItem.total_price.amount}</div>
                      <div className="text-primary ml-2">{packageItem.total_price.currency}{!isDateSelected && '~'}</div>
                    </div>
                  ) : (
                    <>
                      <div className="d-flex align-items-baseline justify-content-end">
                        <div className="text-primary mr-2">{packageItem.total_price.currency}</div>
                        <div className={styles.hotelPrice}>{packageItem.total_price.amount}{!isDateSelected && '~'}</div>
                      </div>
                      <div className="d-flex align-items-baseline justify-content-end">
                        <div className='text-sm text-muted'>{packageItem.total_price_ja.amount}{!isDateSelected && '~'}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <p className={styles.hotelOneNight}>
                {peopleAndStayCountDisplay(hotel.minimum_stay_days)}
              </p>
              <p className={styles.hotelOneNight}>
                {packageItem.ticket_splits.adults + packageItem.ticket_splits.children === 1 ? (
                  `${i18n.t('stay_packages.shared.with_passes')} : ${i18n.t('stay_packages.shared.x_passes.one')}`
                ) : (
                  `${i18n.t('stay_packages.shared.with_passes')} : ${i18n.t('stay_packages.shared.x_passes.other', { x: packageItem.ticket_splits.adults + packageItem.ticket_splits.children })}`
                ) }
              </p>
            </li>
          ))}
        </ul>
      </div>
      <div className="container py-3">
        {hotel.yado_rooms.length > 1 &&
          <p className={styles.hotelOtherPlansCount}>
            {hotel.yado_rooms.length === 1 ? i18n.t('accommodations.x_other_plans_available.one') : i18n.t('accommodations.x_other_plans_available.other', { x: hotel.yado_rooms.length - 1 })}
          </p>
        }
        {(!isDateSelected && hotel.minimum_stay_days > 1) &&
          <div className={styles.minimumStayInfo}>
            <img src={getImage(infoSvg)} className="mr-2" alt="" />
            <div>{i18n.t('stay_packages.hotels.index.reservations_accepted_from', {days: hotel.minimum_stay_days})}</div>
          </div>
        }
        <button
          type='button'
          className="col-12 btn btn-primary rounded-pill font-weight-bold d-block d-md-none"
          >
            {i18n.t('stay_packages.hotels.index.details_reservation')}
        </button>
      </div>
    </div>
  );
};

export default HotelItem;
